import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'

import React, { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { EditIcon, SearchIcon } from 'src/v3/core/icons'
import { CopyToClipboard } from 'src/v3/components/copy-to-clipboard'
import {
  fetchUsers,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectLinkedServiceById,
  selectAccessLevelsStatus,
  fetchAccessLevels,
  selectAccessLevelLinkedServicesStatus,
  fetchAccessLevelLinkedServices,
  fetchLinkedServices,
  selectLinkedServiceRosterStatusByID,
  fetchLinkedServiceRoster,
  selectSelectedLinkedService,
  selectErrorMessage,
  selectedLinkedServiceChanged,
  errorMessageChanged,
  selectSuccessMessage,
  successMessageChanged,
} from '../admin.slice'
import AccessLevel from './access-level'
import BasicInfo from './basic-info'
import Status from './status'
import CredentialVerification from './credential-verification'
import LicenseVerification from './license-verification'
import NREMTVerification from './nremt-verification'
import UserCap from './user-cap'
import BulkUploadUsers from './bulk-upload-users'
import SSO from './sso'
import { CreateLinkedServiceForm } from './forms/create'
import { LinkedServiceAddressForm } from './forms/address'
import { SsoForm } from './forms/sso'
import { UserCapForm } from './forms/user-cap'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let [open, setOpen] = useState(false)
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)
  let errorMessage = useAppSelector(selectErrorMessage)
  let successMessage = useAppSelector(selectSuccessMessage)
  let linkedServices = useAppSelector(selectLinkedServices)
  let [searchValue, setSearchValue] = useState<string>('')
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let linkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedService),
  )
  let accessLevelsStatus = useAppSelector(selectAccessLevelsStatus)
  let accessLevelLinkedServicesStatus = useAppSelector(
    selectAccessLevelLinkedServicesStatus,
  )
  let linkedServiceRosterStatus = useAppSelector(
    selectLinkedServiceRosterStatusByID(selectedLinkedService),
  )

  let [updating, setUpdating] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<string>('')

  useEffect(() => {
    if (accessLevelsStatus === 'uninitialized') {
      dispatch(fetchAccessLevels())
    }
  }, [accessLevelsStatus])

  useEffect(() => {
    if (
      selectedLinkedService &&
      (!linkedServiceRosterStatus ||
        linkedServiceRosterStatus === 'uninitialized')
    ) {
      dispatch(fetchLinkedServiceRoster(selectedLinkedService))
    }
  }, [selectedLinkedService, linkedServiceRosterStatus])

  useEffect(() => {
    if (accessLevelLinkedServicesStatus === 'uninitialized') {
      dispatch(fetchAccessLevelLinkedServices())
    }
  }, [accessLevelLinkedServicesStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  return (
    <>
      <Container maxWidth="xl" className="p-4 sm:p-6">
        <Box className="mb-8">
          <Typography variant="h4" component="h1" className="mb-2">
            Linked Services
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            className="mb-6"
          >
            Manage linked services and configure their settings
          </Typography>
        </Box>
        <Box className="flex items-center gap-4 mb-4">
          <Button
            onClick={() => setFormOpen('create')}
            color="primary"
            variant="contained"
          >
            Create Linked Service
          </Button>
          <Typography variant="body2" color="textSecondary">
            or search existing linked services:
          </Typography>
        </Box>
        <Autocomplete
          open={open}
          onOpen={() => {
            setOpen(true)
          }}
          onClose={() => {
            setOpen(false)
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => `${option.name} | ${option.id}`}
          onInputChange={(_, value) =>
            dispatch(selectedLinkedServiceChanged(value.split(' | ')[1]))
          }
          options={linkedServices}
          loading={linkedServicesStatus === 'pending'}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(e) => setSearchValue(e.target.value)}
              label="Linked Service Name"
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  sx: { backgroundColor: 'background.paperDark' },
                  endAdornment: (
                    <>
                      {
                        <SearchIcon
                          className="cursor-pointer"
                          onClick={() => dispatch(fetchUsers(searchValue))}
                        />
                      }
                      {params.slotProps.input.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        {linkedService && (
          <Box className="grid grid-cols-12 gap-6 mt-6">
            <Box className="col-span-12 md:col-span-4 lg:col-span-3">
              <BasicInfo isSaving={(bool) => setUpdating(bool)} />
            </Box>
            <Box className="col-span-12 md:col-span-8 lg:col-span-9">
              <Paper className="p-8">
                <Box className="mb-4">
                  <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        className="mb-2 font-semibold"
                      >
                        Linked Service ID
                      </Typography>
                      <Box className="flex items-center gap-2">
                        <code className="inline-flex items-center justify-between gap-4 p-2 bg-gray-800 rounded-lg">
                          <span className="text-xs">{linkedService?.id}</span>
                          <CopyToClipboard
                            textToCopy={linkedService?.id || ''}
                            messageLabel="Linked Service ID"
                          />
                        </code>
                      </Box>
                    </Box>

                    <Box className="mb-4">
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        className="mb-2 font-semibold"
                      >
                        Address
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setFormOpen('address')}
                          className="min-w-0 p-1 ml-2"
                        >
                          <EditIcon className="text-base" />
                        </Button>
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {!linkedService?.address &&
                        !linkedService?.city &&
                        (!linkedService?.state ||
                          linkedService.state === 'Other') &&
                        !linkedService?.zip ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ fontStyle: 'italic' }}
                          >
                            No address on file
                          </Typography>
                        ) : (
                          <>
                            {linkedService.address && (
                              <>
                                {linkedService.address}
                                <br />
                              </>
                            )}
                            {linkedService.city && <>{linkedService.city}, </>}{' '}
                            {linkedService.state &&
                            linkedService.state !== 'Other'
                              ? `${linkedService.state} `
                              : ''}
                            {linkedService.zip}
                          </>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box className="mb-8">
                  <Box className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Box>
                      <Typography
                        variant="h5"
                        color="textSecondary"
                        className="mb-2 font-semibold"
                      >
                        Settings
                      </Typography>
                      <Status setFormOpen={setFormOpen} formOpen={formOpen} />
                      <CredentialVerification
                        setFormOpen={setFormOpen}
                        formOpen={formOpen}
                      />
                      <LicenseVerification
                        setFormOpen={setFormOpen}
                        formOpen={formOpen}
                      />
                      <NREMTVerification
                        setFormOpen={setFormOpen}
                        formOpen={formOpen}
                      />
                      <Box className="mb-8">
                        <SSO
                          setFormOpen={setFormOpen}
                          confirmOpen={formOpen}
                          isSaving={(bool) => setUpdating(bool)}
                        />
                      </Box>
                      <Box sx={{ maxWidth: '200px' }}>
                        <AccessLevel isSaving={(bool) => setUpdating(bool)} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography
                        variant="h5"
                        color="textSecondary"
                        className="mb-2 font-semibold"
                      >
                        User Settings
                      </Typography>
                      <UserCap setFormOpen={setFormOpen} />
                      <BulkUploadUsers />
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Container>
      <CreateLinkedServiceForm
        isOpen={formOpen === 'create'}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => setFormOpen('')}
      />
      <LinkedServiceAddressForm
        isOpen={formOpen === 'address'}
        linkedService={linkedService}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => setFormOpen('')}
      />
      <SsoForm
        isOpen={formOpen === 'sso'}
        organizationID={linkedService?.organizationID}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => setFormOpen('')}
      />
      <UserCapForm
        isOpen={formOpen === 'user-cap'}
        userCap={linkedService?.userCap}
        isSaving={(bool) => setUpdating(bool)}
        onClose={() => setFormOpen('')}
      />
      <Backdrop open={updating} onClick={() => setUpdating(false)}>
        <CircularProgress />
      </Backdrop>
      <Snackbar
        open={!!successMessage}
        onClose={() => dispatch(successMessageChanged(''))}
      >
        <Alert
          variant="filled"
          onClose={() => dispatch(successMessageChanged(''))}
          severity="success"
        >
          {successMessage}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMessage}
        onClose={() => dispatch(errorMessageChanged(''))}
      >
        <Alert
          variant="filled"
          onClose={() => dispatch(errorMessageChanged(''))}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
